import React, { useMemo, useCallback, useRef, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import s from './ModernCheckbox.module.scss';

/**
 * ModernCheckbox
 * A fast, accessible checkbox table with instant selection and proper alignment.
 *
 * Props:
 * - data: Array of row objects. Each row MUST have a unique `SKEY` (string/number).
 * - columns: Array of column keys to display (strings).
 * - columnLabels: Map of column key -> label (optional).
 * - selectedKeys: Set<string|number> or array of SKEYs that are selected.
 * - onSelectionChange: function(event, payload) -> void
 *      payload for single toggle: { row, checked }
 *      payload for select-all:    { isSelectAll: true, targetState, rows }
 * - disable: boolean to disable interaction (optional)
 * - showSelectAll: boolean (default true)
 * - searchText: current search string (optional)
 * - onSearchChange: function(newText) (optional)
 * - controlData: optional strings { emptyMessage, emptyResults, showTable }
 */
export default function ModernCheckbox({
  data = [],
  columns = [],
  columnLabels = {},
  selectedKeys = [],
  onSelectionChange,
  disable = false,
  showSelectAll = true,
  searchText = '',
  onSearchChange,
  controlData = { emptyMessage: 'No items yet.', emptyResults: 'No results.', showTable: true },
}) {
  // normalize selectedKeys to a Set for O(1) checks
  const selectedSet = useMemo(() => new Set(Array.isArray(selectedKeys) ? selectedKeys : [...selectedKeys]), [selectedKeys]);

  // filter by search (client-side; keep simple)
  const filteredData = useMemo(() => {
    if (!searchText) return data;
    const q = searchText.toLowerCase();
    return data.filter(row => {
      // check label and visible columns
      const label = (row.label ?? '').toString().toLowerCase();
      if (label.includes(q)) return true;
      return columns.some(k => String(row[k] ?? '').toLowerCase().includes(q));
    });
  }, [data, searchText, columns]);

  // derived: all selected / indeterminate for filtered set
  const { allSelected, indeterminate, selectedCount } = useMemo(() => {
    if (filteredData.length === 0) return { allSelected: false, indeterminate: false, selectedCount: 0 };
    let count = 0;
    for (const row of filteredData) if (selectedSet.has(row.SKEY)) count++;
    return {
      allSelected: count === filteredData.length && filteredData.length > 0,
      indeterminate: count > 0 && count < filteredData.length,
      selectedCount: count,
    };
  }, [filteredData, selectedSet]);

  // handlers
  const handleSearchChange = useCallback((e) => {
    onSearchChange && onSearchChange(e.target.value);
  }, [onSearchChange]);

  const clearSearch = useCallback(() => {
    onSearchChange && onSearchChange('');
  }, [onSearchChange]);

  const onRowToggle = useCallback((e, row) => {
    onSelectionChange && onSelectionChange(e, { row, checked: e.target.checked });
  }, [onSelectionChange]);

  const selectAllRef = useRef(null);
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  const handleSelectAll = useCallback(() => {
    if (!onSelectionChange || filteredData.length === 0) return;
    const next = !allSelected;
    const synthetic = { target: { checked: next } };
    onSelectionChange(synthetic, { isSelectAll: true, targetState: next, rows: filteredData });
  }, [onSelectionChange, filteredData, allSelected]);

  // memoized row to avoid re-renders
  const Row = memo(function Row({ row }) {
    const checked = selectedSet.has(row.SKEY);
    const rowClasses = checked ? `${s.tableRow} ${s.selected}` : s.tableRow;
    return (
      <tr className={rowClasses}>
        <td className={s.checkboxCell}>
          <label className={s.tableCheckboxContainer}>
            <input
              aria-label={`Select ${row.label ?? row.SKEY}`}
              type="checkbox"
              className={s.tableCheckboxInput}
              checked={checked}
              onChange={(e) => onRowToggle(e, row)}
              disabled={disable || row.disable}
            />
            <span className={s.tableCheckmark} />
          </label>
        </td>
        {columns.map((col) => (
          <td key={String(col)} className={s.tableCell}>
            <span className={s.cellContent}>{row[col] ?? '-'}</span>
          </td>
        ))}
      </tr>
    );
  });

  return (
    <div className={s.modernCheckbox}>
      {/* Search */}
      {onSearchChange ? (
        <div className={s.searchBar}>
          <input
            value={searchText}
            onChange={handleSearchChange}
            className={s.searchInput}
            type="text"
            placeholder="Search…"
            disabled={disable}
            aria-label="Search items"
          />
          {searchText && (
            <button type="button" className={s.clearBtn} onClick={clearSearch} disabled={disable} aria-label="Clear search">
              ×
            </button>
          )}
        </div>
      ) : null}

      {/* Select All */}
      {showSelectAll && filteredData.length > 0 && (
        <div className={s.selectAllSection}>
          <label className={s.selectAllContainer}>
            <input
              type="checkbox"
              ref={selectAllRef}
              className={s.selectAllInput}
              checked={allSelected}
              onChange={handleSelectAll}
              disabled={disable}
              aria-label="Select all filtered"
            />
            <span className={s.selectAllCheckmark} />
            <span className={s.selectAllText}>Select All ({selectedCount}/{filteredData.length})</span>
          </label>
        </div>
      )}

      {/* Table / List */}
      {controlData.showTable !== false ? (
        <div className={s.tableContainer}>
          {filteredData.length === 0 ? (
            <div className={s.emptyMessage}>{searchText ? (controlData.emptyResults || 'No results.') : (controlData.emptyMessage || 'No items.')}</div>
          ) : (
            <table className={s.dataTable}>
              <thead>
                <tr>
                  <th className={s.checkboxColumn}>
                    <span className={s.visuallyHidden}>Select</span>
                  </th>
                  {columns.map((col) => (
                    <th key={String(col)} className={s.tableHeader}>
                      {columnLabels[col] ?? String(col)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredData.map((row) => (
                  <Row key={String(row.SKEY)} row={row} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <ul className={`${s.wsaCheckbox} ${s.checkbox}`}>
          {filteredData.length === 0 ? (
            <div className={s.emptyMessage}>{searchText ? (controlData.emptyResults || 'No results.') : (controlData.emptyMessage || 'No items.')}</div>
          ) : (
            filteredData.map((row) => {
              const checked = selectedSet.has(row.SKEY);
              return (
                <li key={'li-' + String(row.SKEY)} className={checked ? 'selected' : ''}>
                  <label className={s.container}>
                    <span className={s.labelText}>{row.label || String(row.SKEY)}</span>
                    <input
                      aria-label={row.label || String(row.SKEY)}
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => onRowToggle(e, row)}
                      disabled={disable || row.disable}
                    />
                    <span className={s.checkmark} />
                  </label>
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}

ModernCheckbox.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  columns: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  columnLabels: PropTypes.object,
  selectedKeys: PropTypes.oneOfType([
    PropTypes.instanceOf(Set),
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
  ]),
  onSelectionChange: PropTypes.func,
  disable: PropTypes.bool,
  showSelectAll: PropTypes.bool,
  searchText: PropTypes.string,
  onSearchChange: PropTypes.func,
  controlData: PropTypes.object,
};
